/**
 * Top-level game state and the controller actions that mutate it.
 *
 * The game state machine follows the original's design:
 *   - The player navigates a menu (main → difficulty → command post).
 *   - From the command post they enter a *unit-driver mode* for a given kind.
 *   - While driving, they have per-kind controls for movement, radar, target
 *     lock, weapon selection, firing, shields, and queries.
 *   - The engine ticks continuously, advancing all moving units and the AI.
 *   - Events the player isn't driving are queued as messages.
 *
 * This module owns no audio and no DOM — it only computes state transitions
 * and emits typed events the UI layer can map to sounds / speech / ARIA.
 */

import {
  DIRECTION_VECTORS,
  Difficulty,
  Direction,
  type Position,
  PlayerId,
  UnitKind,
  chebyshev,
  isOnBoard,
} from "./types.js";
import { Board } from "./grid.js";
import {
  type RadarPointer,
  type ShieldPosition,
  type Unit,
  FACTORY_MINERAL_CAP,
  HARD_SLOT_CAP,
  UNIT_DATA,
  createUnit,
  shieldBudget,
  startingMinerals,
  unitCapForDifficulty,
} from "./units.js";
import {
  type AttackResult,
  type BaseStrike,
  applyHit,
  resolveBaseStrike,
  targetClassVisible,
} from "./combat.js";
import { type AiState, type AiTickEvents, makeAiState, tickAi } from "./ai.js";
import { type Rng, makeRng } from "./rng.js";
import { type WeaponSpec, WEAPONS, recommendWeapon, shipHeight, weaponAt } from "./weapons.js";
import { MessageQueue } from "./messages.js";

/** Reason a `produce()` call failed, useful for the UI. */
export type ProduceError =
  | "insufficient_minerals"
  | "slot_cap_reached"
  | "difficulty_cap_reached"
  | "no_factory_room";

export type Outcome = "ongoing" | "human_wins" | "opponent_wins";

/** What kind of cell the active unit just received in its incoming-shell ear. */
export interface IncomingShot {
  /** Side the shot is arriving from, so the player can point their shield. */
  direction: "left" | "center" | "right";
  /** Sound id to play (laser_shell / electronic_shell / gravitation_torpido). */
  sound: string;
  /** Did the player's shield (correctly positioned) save them? */
  blocked: boolean;
  /** Did this kill the active unit? */
  killed: boolean;
}

export interface FireOutcome {
  /** True iff there was a target locked when fire was attempted. */
  hadLock: boolean;
  /** True iff the player's pointer was on-target ("unbroken" radar). */
  onTarget: boolean;
  /** True iff the selected weapon was right for the locked target. */
  rightWeapon: boolean;
  /** What happened — hit / absorbed / killed / no-effect. */
  attack: AttackResult | null;
  /** Sound to play (weapon sound on success / "not" on miss / silent on wrong weapon). */
  sound: string | null;
}

export interface GameEvents {
  /** AI attacks resolved this tick (not directly aimed at active unit). */
  aiAttacks: AttackResult[];
  /** A shot aimed at the active unit (if any). */
  incoming: IncomingShot | null;
  /** Search radar acquired a target this tick (active unit only). */
  targetAcquired: { targetKind: UnitKind; targetPos: Position } | null;
  /** Player's base just got struck. */
  yourBaseStrike: BaseStrike | null;
  /** Enemy base just got struck by player. */
  enemyBaseStrike: BaseStrike | null;
  /** Current outcome. */
  outcome: Outcome;
}

const EMPTY_EVENTS: GameEvents = {
  aiAttacks: [],
  incoming: null,
  targetAcquired: null,
  yourBaseStrike: null,
  enemyBaseStrike: null,
  outcome: "ongoing",
};

export interface GameInit {
  difficulty: Difficulty;
  seed: number;
  humanBase?: Position;
  opponentBase?: Position;
  opponentRoster?: ReadonlyArray<{
    kind: UnitKind;
    position: Position;
    direction?: number;
  }>;
}

/** Pick two base cells at least 40 cells apart with a 2-cell border buffer. */
export function pickBasePositions(rng: Rng): [Position, Position] {
  const BUFFER = 2;
  const MIN_GAP = 40;
  for (let attempt = 0; attempt < 1024; attempt++) {
    const a: Position = {
      x: rng.range(BUFFER, 99 - BUFFER),
      y: rng.range(BUFFER, 99 - BUFFER),
    };
    const b: Position = {
      x: rng.range(BUFFER, 99 - BUFFER),
      y: rng.range(BUFFER, 99 - BUFFER),
    };
    if (Math.abs(a.x - b.x) >= MIN_GAP || Math.abs(a.y - b.y) >= MIN_GAP) {
      return [a, b];
    }
  }
  return [
    { x: BUFFER, y: BUFFER },
    { x: 99 - BUFFER, y: 99 - BUFFER },
  ];
}

/** How often (in ticks) a worker produces one mineral when adjacent to base. */
const WORKER_MINING_INTERVAL = 4;

export class Game {
  readonly difficulty: Difficulty;
  readonly board: Board;
  readonly rng: Rng;
  readonly humanUnits: Unit[] = [];
  readonly opponentUnits: Unit[] = [];
  readonly messages = new MessageQueue();
  /**
   * The player's single mineral count, capped at FACTORY_MINERAL_CAP (100).
   * Per MECHANICS.md §9 the original has one DAT_00491f80 counter — there
   * is no separate "spendable" vs "factory" pool. Workers add to it; the
   * factory consumes from it on production.
   */
  humanMinerals: number;
  humanScore = 0;
  opponentScore = 0;
  tickCount = 0;
  outcome: Outcome = "ongoing";
  /** True while a static unit (gun/plant) needs placement before any other action. */
  pendingPlacementUnitId: number | null = null;
  /** Flips to true once one of the player's scouts has spotted the enemy base. */
  enemyBaseKnown = false;

  /** Which unit kind the player is currently driving (null = command post). */
  driverKind: UnitKind | null = null;

  private nextUnitId = 1;
  private readonly aiState: AiState;
  /** Per-kind active unit id ("current" worker, current soldier, etc.). */
  private readonly activeByKind = new Map<UnitKind, number>();
  /** Workers' last-mined tick, for the throttle. */
  private readonly minedAtTick = new Map<number, number>();

  constructor(init: GameInit) {
    this.difficulty = init.difficulty;
    this.rng = makeRng(init.seed);
    const [humanBase, opponentBase] = (() => {
      if (init.humanBase && init.opponentBase) return [init.humanBase, init.opponentBase];
      return pickBasePositions(this.rng);
    })();
    this.board = new Board(humanBase, opponentBase);
    this.humanMinerals = startingMinerals(this.difficulty);
    this.aiState = makeAiState(humanBase, this.difficulty === Difficulty.Easy);

    for (const seed of init.opponentRoster ?? []) {
      const u = createUnit({
        id: this.nextUnitId++,
        kind: seed.kind,
        owner: PlayerId.Opponent,
        position: seed.position,
        ...(seed.direction !== undefined ? { direction: seed.direction } : {}),
      });
      if (this.board.place(u)) this.opponentUnits.push(u);
    }
  }

  // ─── Roster queries ───────────────────────────────────────────────────────

  /** All alive units of `kind` owned by the player, in spawn order. */
  unitsOfKind(kind: UnitKind, owner: 0 | 1 = PlayerId.Human): readonly Unit[] {
    const pool = owner === PlayerId.Human ? this.humanUnits : this.opponentUnits;
    return pool.filter((u) => u.alive && u.kind === kind);
  }

  /** The "current" unit of `kind`, defaulting to the first alive one. */
  activeUnit(kind?: UnitKind): Unit | null {
    const k = kind ?? this.driverKind;
    if (k === null) return null;
    const alive = this.unitsOfKind(k);
    if (alive.length === 0) return null;
    const id = this.activeByKind.get(k);
    if (id !== undefined) {
      const u = alive.find((u) => u.id === id);
      if (u) return u;
    }
    const first = alive[0]!;
    this.activeByKind.set(k, first.id);
    return first;
  }

  /** Pick the nth alive unit of the driver's kind (1-based; 0 maps to 10). */
  switchActiveSlot(slot: number): Unit | null {
    if (this.driverKind === null) return null;
    const alive = this.unitsOfKind(this.driverKind);
    const idx = slot === 0 ? 9 : slot - 1;
    const u = alive[idx];
    if (!u) return null;
    this.activeByKind.set(this.driverKind, u.id);
    return u;
  }

  // ─── Menu transitions ─────────────────────────────────────────────────────

  /** Enter the driver mode for `kind`. Returns false if no live units exist. */
  enterDriver(kind: UnitKind): boolean {
    const alive = this.unitsOfKind(kind);
    if (alive.length === 0) return false;
    this.driverKind = kind;
    if (!this.activeByKind.has(kind)) {
      this.activeByKind.set(kind, alive[0]!.id);
    }
    return true;
  }

  /** Leave driver mode and return to the command post menu. */
  exitDriver(): void {
    this.driverKind = null;
  }

  // ─── Factory ──────────────────────────────────────────────────────────────

  produce(kind: UnitKind): { ok: true; unit: Unit } | { ok: false; reason: ProduceError } {
    const data = UNIT_DATA[kind];
    if (this.humanMinerals < data.cost) {
      return { ok: false, reason: "insufficient_minerals" };
    }
    const slotCount = this.unitsOfKind(kind).length;
    if (slotCount >= HARD_SLOT_CAP) {
      return { ok: false, reason: "slot_cap_reached" };
    }
    if (kind !== UnitKind.Worker && kind !== UnitKind.Truck) {
      if (slotCount >= unitCapForDifficulty(this.difficulty)) {
        return { ok: false, reason: "difficulty_cap_reached" };
      }
    }
    // Per help.txt: "First position of the new <static unit> will be the
    // position of your base." Static units spawn ON the base cell so the
    // player moves them ±2 cells from there before placing. Movers spawn
    // on an adjacent empty cell instead.
    const base = this.board.bases[PlayerId.Human];
    const spawnAt = UNIT_DATA[kind].mobile ? this.findSpawnCell(base) : { ...base };
    if (!spawnAt) return { ok: false, reason: "no_factory_room" };
    this.humanMinerals -= data.cost;
    const unit = createUnit({
      id: this.nextUnitId++,
      kind,
      owner: PlayerId.Human,
      position: spawnAt,
    });
    // Static units (gun, plant) need placement before they're useful. They
    // share their cell with the base until placed, so don't add them to the
    // board's occupancy grid yet.
    if (!UNIT_DATA[kind].mobile) {
      unit.placed = false;
      this.pendingPlacementUnitId = unit.id;
    } else {
      this.board.place(unit);
    }
    this.humanUnits.push(unit);
    this.activeByKind.set(kind, unit.id);
    return { ok: true, unit };
  }

  private findSpawnCell(centre: Position): Position | null {
    for (let r = 1; r <= 5; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          const cand: Position = { x: centre.x + dx, y: centre.y + dy };
          if (isOnBoard(cand) && this.board.isEmpty(cand)) return cand;
        }
      }
    }
    return null;
  }

  /**
   * Place the pending static unit at `pos`. Must be within 2 cells of base
   * and on an empty cell.
   */
  placeStaticAt(pos: Position): boolean {
    if (this.pendingPlacementUnitId === null) return false;
    const u = this.humanUnits.find((x) => x.id === this.pendingPlacementUnitId);
    if (!u) return false;
    const base = this.board.bases[PlayerId.Human];
    if (chebyshev(pos, base) > 2) return false;
    if (pos.x === base.x && pos.y === base.y) return false; // base cell forbidden
    if (!this.board.isEmpty(pos)) return false;
    u.position = pos;
    if (!this.board.place(u)) return false;
    u.placed = true;
    this.pendingPlacementUnitId = null;
    return true;
  }

  // ─── Driver actions: movement ─────────────────────────────────────────────

  /** Start moving the active mover, optionally for a set number of cells. */
  startMove(cells: number = -1): boolean {
    const u = this.activeUnit();
    if (!u || !UNIT_DATA[u.kind].mobile) return false;
    if (this.pendingPlacementUnitId === u.id) return false;
    u.move = { moving: true, cellsRemaining: cells };
    u.searchRadarOn = false; // can't scan while moving
    u.targetUnitId = null;
    return true;
  }

  /** Stop the active mover. */
  stopMove(): void {
    const u = this.activeUnit();
    if (!u) return;
    u.move = { moving: false, cellsRemaining: 0 };
  }

  /** Turn the active mover one step counter-clockwise (8-way). */
  turnLeft(): void {
    const u = this.activeUnit();
    if (!u || !UNIT_DATA[u.kind].mobile) return;
    u.direction = ((u.direction - 1 - 1 + 8) % 8) + 1;
  }

  /** Turn the active mover one step clockwise. */
  turnRight(): void {
    const u = this.activeUnit();
    if (!u || !UNIT_DATA[u.kind].mobile) return;
    u.direction = (u.direction % 8) + 1;
  }

  /** Teleport a soldier/tank/cruiser back to base (costs 25 energy). */
  teleportToBase(): boolean {
    const u = this.activeUnit();
    if (!u) return false;
    if (u.kind !== UnitKind.Soldier && u.kind !== UnitKind.SpaceTank && u.kind !== UnitKind.SpaceCruiser) {
      return false;
    }
    const teleportCost = 25;
    if (u.energy < teleportCost) {
      // Insufficient energy → unit annihilates.
      this.killHumanUnit(u);
      return false;
    }
    u.energy -= teleportCost;
    const base = this.board.bases[PlayerId.Human];
    // Drop on a free cell adjacent to base.
    const slot = this.findSpawnCell(base);
    if (!slot) return false;
    this.board.moveTo(u, slot);
    u.move = { moving: false, cellsRemaining: 0 };
    return true;
  }

  // ─── Driver actions: radar + target + weapon ──────────────────────────────

  /** R key — toggle the search radar. Movers must be stopped first. */
  toggleSearchRadar(): boolean {
    const u = this.activeUnit();
    if (!u) return false;
    if (UNIT_DATA[u.kind].sightRange === 0) return false;
    if (UNIT_DATA[u.kind].mobile && u.move.moving) return false;
    u.searchRadarOn = !u.searchRadarOn;
    if (!u.searchRadarOn) {
      u.targetUnitId = null;
      u.targetPointer = "center";
    }
    return true;
  }

  /** L/R arrows steer the target radar pointer. */
  moveTargetRadar(dir: "left" | "right"): void {
    const u = this.activeUnit();
    if (!u || u.targetUnitId === null) return;
    u.targetPointer = stepPointer(u.targetPointer, dir);
  }

  openWeaponMenu(): boolean {
    const u = this.activeUnit();
    if (!u) return false;
    if (WEAPONS[u.kind].length === 0) return false;
    if (u.shieldTicks > 0 && (u.kind === UnitKind.ElectronicGun ||
                              u.kind === UnitKind.RocketPlant ||
                              u.kind === UnitKind.SpaceTank ||
                              u.kind === UnitKind.SpaceCruiser)) {
      // Some unit types disable shield when the weapon menu opens.
      u.shieldTicks = 0;
    }
    u.weaponMenuOpen = true;
    u.weaponSelected = u.weaponSelected ?? 0;
    return true;
  }

  closeWeaponMenu(): void {
    const u = this.activeUnit();
    if (!u) return;
    u.weaponMenuOpen = false;
  }

  /** Choose a weapon by index in the active unit's arsenal. */
  selectWeapon(index: number): WeaponSpec | null {
    const u = this.activeUnit();
    if (!u) return null;
    const w = weaponAt(u.kind, index);
    if (!w) return null;
    u.weaponSelected = index;
    return w;
  }

  /** Fire the currently selected weapon at the locked target. */
  fire(): FireOutcome {
    const u = this.activeUnit();
    if (!u) return { hadLock: false, onTarget: false, rightWeapon: false, attack: null, sound: null };
    const out: FireOutcome = {
      hadLock: false,
      onTarget: false,
      rightWeapon: false,
      attack: null,
      sound: null,
    };
    if (u.targetUnitId === null) return out;
    out.hadLock = true;
    out.onTarget = u.targetPointer === "center";
    if (!out.onTarget) return out;
    const target = this.opponentUnits.find((x) => x.id === u.targetUnitId);
    if (!target || !target.alive) {
      u.targetUnitId = null;
      return out;
    }
    if (u.weaponSelected === null) return out;
    const w = weaponAt(u.kind, u.weaponSelected);
    if (!w) return out;
    const distance = chebyshev(u.position, target.position);
    const ctx = { height: shipHeight(target.kind, target.position) };
    out.rightWeapon = w.isAppropriate(target, distance, ctx);
    out.sound = w.sound;
    if (!out.rightWeapon) {
      // Wrong weapon: silent failure, energy lost.
      u.energy = Math.max(0, u.energy - 5);
      return out;
    }
    const r = applyHit(u, target, this.difficulty);
    out.attack = r;
    if (r.killed) {
      this.humanScore += r.score;
      this.board.remove(target);
      // Clear our lock since the target's gone.
      u.targetUnitId = null;
      u.targetPointer = "center";
    }
    return out;
  }

  // ─── Driver actions: shield ───────────────────────────────────────────────

  /** S key — toggle the energetic shield on the active unit. */
  toggleShield(): void {
    const u = this.activeUnit();
    if (!u) return;
    if (u.shieldTicks > 0) {
      u.shieldTicks = 0;
    } else {
      u.shieldTicks = shieldBudget(this.difficulty);
    }
  }

  /** L/R arrows when shield is active and weapon menu is closed → reposition. */
  moveShield(dir: "left" | "right"): void {
    const u = this.activeUnit();
    if (!u || u.shieldTicks === 0) return;
    if (!UNIT_DATA[u.kind].mobile) return; // Static units have omnidirectional shields.
    u.shieldPosition = nextShieldPos(u.shieldPosition, dir);
  }

  // ─── Strike opponent base ─────────────────────────────────────────────────

  /** Delete key — strike enemy base with active unit. */
  strikeOpponentBase(): BaseStrike | null {
    const u = this.activeUnit();
    if (!u || !u.alive || !UNIT_DATA[u.kind].canStrikeBase) return null;
    const base = this.board.bases[PlayerId.Opponent];
    if (u.position.x !== base.x || u.position.y !== base.y) return null;
    const strike = resolveBaseStrike(u, this.difficulty);
    this.humanScore += strike.score;
    if (strike.gameOver) this.outcome = "human_wins";
    return strike;
  }

  // ─── Tick ─────────────────────────────────────────────────────────────────

  tick(): GameEvents {
    if (this.outcome !== "ongoing") return { ...EMPTY_EVENTS, outcome: this.outcome };
    this.tickCount += 1;

    // 1. Continuous movement for all human movers (active + background).
    for (const u of this.humanUnits) {
      if (!u.alive) continue;
      if (!UNIT_DATA[u.kind].mobile) continue;
      if (this.pendingPlacementUnitId === u.id) continue;
      if (!u.move.moving) continue;
      const vec = DIRECTION_VECTORS[u.direction as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8];
      if (!vec) continue;
      const dest: Position = { x: u.position.x + vec[0], y: u.position.y + vec[1] };
      const offBoard = !isOnBoard(dest);
      if (offBoard) {
        if (this.difficulty === Difficulty.Easy) {
          // Easy mode: stop at edge.
          u.move = { moving: false, cellsRemaining: 0 };
          continue;
        }
        // Hard+: lose the unit.
        this.killHumanUnit(u);
        continue;
      }
      if (!this.board.isEmpty(dest)) {
        // Blocked — stop in place.
        u.move = { moving: false, cellsRemaining: 0 };
        continue;
      }
      this.board.moveTo(u, dest);
      if (u.move.cellsRemaining > 0) {
        u.move.cellsRemaining -= 1;
        if (u.move.cellsRemaining === 0) u.move.moving = false;
      }
    }

    // 2. Workers near base auto-add a mineral every WORKER_MINING_INTERVAL ticks.
    let mined = 0;
    for (const u of this.humanUnits) {
      if (!u.alive || u.kind !== UnitKind.Worker) continue;
      const last = this.minedAtTick.get(u.id) ?? 0;
      if (this.tickCount - last < WORKER_MINING_INTERVAL) continue;
      this.minedAtTick.set(u.id, this.tickCount);
      mined += 1;
    }
    if (mined > 0) {
      const before = this.humanMinerals;
      this.humanMinerals = Math.min(FACTORY_MINERAL_CAP, this.humanMinerals + mined);
      if (before < FACTORY_MINERAL_CAP && this.humanMinerals >= FACTORY_MINERAL_CAP) {
        this.messages.push({
          kind: "minerals_full",
          text: "The factory is full of minerals.",
          tick: this.tickCount,
        });
      }
    }

    // 2b. Scout discovers enemy base — flips enemyBaseKnown once a scout
    // gets within its sight range of the enemy base cell.
    if (!this.enemyBaseKnown) {
      const enemyBase = this.board.bases[PlayerId.Opponent];
      const scoutRange = UNIT_DATA[UnitKind.SpaceScout].sightRange;
      for (const u of this.humanUnits) {
        if (!u.alive || u.kind !== UnitKind.SpaceScout) continue;
        if (chebyshev(u.position, enemyBase) <= scoutRange) {
          this.enemyBaseKnown = true;
          this.messages.push({
            kind: "enemy_base_found",
            text: `Enemy base found at ${enemyBase.x}, ${enemyBase.y}.`,
            tick: this.tickCount,
          });
          break;
        }
      }
    }

    // 3. Search radar scans for the active unit (and other own-side units).
    let targetAcquired: GameEvents["targetAcquired"] = null;
    const active = this.activeUnit();
    for (const u of this.humanUnits) {
      if (!u.alive) continue;
      if (!u.searchRadarOn) continue;
      if (u.targetUnitId !== null) continue;
      const target = findScanTarget(u, this.opponentUnits);
      if (!target) continue;
      u.targetUnitId = target.id;
      u.targetPointer = this.rng.next() < 0.5 ? "left" : "right";
      if (u.id === active?.id) {
        targetAcquired = { targetKind: target.kind, targetPos: { ...target.position } };
      } else {
        this.messages.push({
          kind: "enemy_spotted",
          text: `Your ${kindName(u.kind)} found an enemy ${kindName(target.kind)} at ${target.position.x}, ${target.position.y}.`,
          tick: this.tickCount,
        });
      }
    }

    // 4. AI tick.
    const aiEvents: AiTickEvents = tickAi(
      this.opponentUnits,
      this.humanUnits,
      this.board,
      this.aiState,
      this.difficulty,
      this.rng,
    );

    // 5. Detect incoming shot at active unit (if AI shot at us).
    let incoming: IncomingShot | null = null;
    if (active && aiEvents.attacks.length > 0) {
      const aimedAtUs = aiEvents.attacks.find((r) => r.target?.id === active.id);
      if (aimedAtUs) {
        const dir = randomIncomingDir(this.rng);
        incoming = {
          direction: dir,
          sound: incomingSoundFor(active.kind),
          blocked: aimedAtUs.absorbed && (
            !UNIT_DATA[active.kind].mobile ||
            (active.shieldTicks > 0 && active.shieldPosition === dir)
          ),
          killed: aimedAtUs.killed,
        };
      }
    }

    // 6. Resolve base-strikes.
    let yourBaseStrike: BaseStrike | null = null;
    if (aiEvents.baseStrike) {
      yourBaseStrike = aiEvents.baseStrike;
      this.opponentScore += aiEvents.baseStrike.score;
      if (aiEvents.baseStrike.wipeLightUnits) {
        for (const u of this.humanUnits) {
          if (u.alive && (u.kind === UnitKind.Worker || u.kind === UnitKind.Truck)) {
            this.killHumanUnit(u);
          }
        }
        this.humanMinerals = Math.min(this.humanMinerals, 15);
        this.messages.push({
          kind: "your_base_struck",
          text: "Enemy tank struck your base. Workers and trucks are gone.",
          tick: this.tickCount,
        });
      }
      if (aiEvents.baseStrike.gameOver) {
        this.outcome = "opponent_wins";
        this.humanScore = 0;
      }
    }

    // 7. Shield decay — drop shieldTicks after a successful block.
    for (const u of [...this.humanUnits, ...this.opponentUnits]) {
      if (u.alive && u.shieldTicks > 0) {
        u.shieldTicks -= 1;
      }
    }

    return {
      aiAttacks: aiEvents.attacks,
      incoming,
      targetAcquired,
      yourBaseStrike,
      enemyBaseStrike: null,
      outcome: this.outcome,
    };
  }

  // ─── Internals ────────────────────────────────────────────────────────────

  private killHumanUnit(u: Unit): void {
    if (!u.alive) return;
    this.board.remove(u);
    u.alive = false;
    if (this.pendingPlacementUnitId === u.id) this.pendingPlacementUnitId = null;
    const message =
      u.kind === UnitKind.Worker || u.kind === UnitKind.Truck
        ? null // light units don't message individually
        : `Your ${kindName(u.kind)} was destroyed.`;
    if (message) {
      this.messages.push({ kind: "unit_destroyed", text: message, tick: this.tickCount });
    }
    // Notify when all workers or all trucks are lost (per help.txt).
    const aliveOf = (k: UnitKind) => this.humanUnits.some((x) => x.alive && x.kind === k);
    if (u.kind === UnitKind.Worker && !aliveOf(UnitKind.Worker)) {
      this.messages.push({
        kind: "all_workers_lost",
        text: "All your workers are gone.",
        tick: this.tickCount,
      });
    }
    if (u.kind === UnitKind.Truck && !aliveOf(UnitKind.Truck)) {
      this.messages.push({
        kind: "all_trucks_lost",
        text: "All your trucks are gone.",
        tick: this.tickCount,
      });
    }
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function stepPointer(p: RadarPointer, dir: "left" | "right"): RadarPointer {
  if (dir === "left") {
    if (p === "right") return "center";
    if (p === "center") return "left";
    return "left";
  }
  if (p === "left") return "center";
  if (p === "center") return "right";
  return "right";
}

function nextShieldPos(p: ShieldPosition, dir: "left" | "right"): ShieldPosition {
  return stepPointer(p, dir);
}

function randomIncomingDir(rng: Rng): "left" | "center" | "right" {
  const r = rng.int(3);
  return r === 0 ? "left" : r === 1 ? "right" : "center";
}

function incomingSoundFor(kind: UnitKind): string {
  // Per help.txt the warning sound matches what the unit is being shot by.
  if (kind === UnitKind.Soldier) return "laser_shell";
  if (kind === UnitKind.ElectronicGun || kind === UnitKind.RocketPlant) return "electronic_shell";
  if (kind === UnitKind.SpaceTank) return "electronic_shell";
  if (kind === UnitKind.SpaceScout || kind === UnitKind.SpaceCruiser) return "gravitation_torpido";
  return "laser_shell";
}

function findScanTarget(scanner: Unit, candidates: readonly Unit[]): Unit | null {
  const range = UNIT_DATA[scanner.kind].sightRange;
  if (range === 0) return null;
  let best: Unit | null = null;
  let bestDist = range + 1;
  for (const u of candidates) {
    if (!u.alive || u.owner === scanner.owner) continue;
    if (!targetClassVisible(scanner.kind, u.kind)) continue;
    const d = chebyshev(scanner.position, u.position);
    if (d > range) continue;
    if (d < bestDist) {
      best = u;
      bestDist = d;
    }
  }
  return best;
}

/** Human-readable kind label for messages / queries. */
export function kindName(kind: UnitKind): string {
  switch (kind) {
    case UnitKind.Worker: return "worker";
    case UnitKind.Truck: return "truck";
    case UnitKind.Soldier: return "soldier";
    case UnitKind.ElectronicGun: return "electronic gun";
    case UnitKind.RocketPlant: return "rocket plant";
    case UnitKind.SpaceTank: return "space tank";
    case UnitKind.SpaceScout: return "space scout";
    case UnitKind.SpaceCruiser: return "space cruiser";
  }
}

// Re-exports the UI may want without crawling our internals.
export { recommendWeapon, WEAPONS };
export type { WeaponSpec };
export { Direction };
